import React, { useMemo, useState } from 'react'
import {
  useModalContext,
  Button,
  Space,
  TextHeading,
  TextBody,
  ButtonGroup,
  Alert,
  Box,
  RadioGroup,
  RadioButton,
  Checkbox,
  ErrorNotification,
  useTriggerSnackbar,
} from '@ally/metronome-ui'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import { Beneficiary } from '../../../types'
import { useIraBeneficiariesApi } from '../../../api/ira-beneficiaries-api'
import { getBeneficiaryDisplayName } from '../../../utils/formatter'
import { useIraBeneficiaries } from '../../../services/IraBeneficiariesProvider'
import { fireErrorTagging } from '../../../utils/tagging'

export const IraDeleteModalContent: React.FC<{
  beneficiaryId: string
  planId: string
  beneficiaries?: Beneficiary[]
}> = ({ beneficiaryId, planId, beneficiaries }) => {
  const { setOpen } = useModalContext()
  const { deleteIraBeneficiary } = useIraBeneficiariesApi()
  const [formError, setFormError] = useState(false)
  const [removeBeneSelectionError, setRemoveBeneSelectionError] =
    useState(false)
  const [signedAndAgreedError, setSignedAndAgreedError] = useState(false)
  const [isSignedAndAgreed, setIsSignedAndAgreed] = useState(false)
  const [removeBeneficiary, setRemoveBeneficiary] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const beneficiariesRemaining = useMemo(() => {
    if (beneficiaries && beneficiaries.length > 0) {
      return beneficiaries.length - 1
    }
    return -1
  }, [beneficiaries])

  const beneficiary = useMemo(() => {
    return (
      beneficiaries &&
      beneficiaries.find(bene => bene.beneficiaryId === beneficiaryId)
    )
  }, [beneficiaryId, beneficiaries])

  const handleCloseModal = (): void => {
    setOpen(false)
    setRemoveBeneSelectionError(false)
    setSignedAndAgreedError(false)
  }

  const { getIraBeneficiaries } = useIraBeneficiaries()
  const { triggerSnackbar } = useTriggerSnackbar()

  const deleteBeneficiaryClick = async (): Promise<void> => {
    if (!removeBeneficiary) {
      setRemoveBeneSelectionError(true)
      fireErrorTagging({ removeBeneficiary: 'Please make a selection.' })
    } else if (removeBeneficiary === 'yes' && isSignedAndAgreed) {
      try {
        setIsLoading(true)
        await deleteIraBeneficiary({ planId, beneficiaryId })
        await getIraBeneficiaries()
        // istanbul ignore next // we have tested bene being deleted, but can't test snackbar
        triggerSnackbar({
          text: 'You removed a beneficiary from your account and we adjusted your funds allocation. Please review these changes.',
          columnSize: 4,
          snackbarButtonText: 'Close',
          onClose: () =>
            requestAnimationFrame(() => {
              document.getElementById(`${planId}-heading`)?.focus()
            }),
        })
      } catch (e) {
        setFormError(true)
        allytm.event(
          'customError',
          'It’s us – we’re having a moment. We couldn’t delete your beneficiary. We’ll be quick about it, so try again in a bit.',
          '',
        )
      } finally {
        setIsLoading(false)
      }
    } else {
      setSignedAndAgreedError(true)
      fireErrorTagging({
        removeBeneficiary: 'Please check this box to continue.',
      })
    }
  }

  const handleChange = (value: string): void => {
    setRemoveBeneficiary(value)
    setRemoveBeneSelectionError(false)
  }

  const handleSignAndAgree = (event: any): void => {
    if (event.target.checked) {
      setSignedAndAgreedError(false)
    }
    setIsSignedAndAgreed(event.target.checked)
  }

  if (!beneficiary || beneficiariesRemaining === -1) return <></>

  return (
    <Space mb="md">
      <TextHeading tag="h1" size="sm" weight="bold" tabIndex={-1}>
        Remove beneficiary?
      </TextHeading>
      <>
        {formError && (
          <Box mb="md">
            <Alert
              variant="error"
              disableContainer
              body="It's us - we're having a moment. We couldn't delete your beneficiary. We'll be quick about it, so try again in a bit."
              focusOnMount
            />
          </Box>
        )}
      </>
      <TextBody tag="p" size="sm" weight="normal">
        Are you sure you want to remove{' '}
        <Private as="span">{getBeneficiaryDisplayName(beneficiary)}</Private>?
      </TextBody>
      <RadioGroup
        name="removeBeneficiary"
        legendText=""
        isLegendHidden
        disableDefaultSelection
        onChange={handleChange}
        errorMessage={
          removeBeneSelectionError ? 'Please make a selection.' : ''
        }
      >
        <RadioButton label="Yes" value="yes" data-testid="yes-remove-option" />
        <RadioButton label="No" value="no" data-testid="no-go-back-option" />
      </RadioGroup>
      {removeBeneficiary === 'yes' && (
        <Box>
          <Box mb="md">
            <TextBody tag="p" size="sm" weight="normal">
              We Need Your Electronic Signature
            </TextBody>
          </Box>
          <Box mb="xl">
            <Checkbox
              name="signAndAgree"
              label="By checking this box, I sign and agree that the information provided is true and correctly reflects my choices."
              data-testid="sign-and-agree-checkbox"
              isChecked={isSignedAndAgreed}
              onChange={handleSignAndAgree}
            />
            {signedAndAgreedError && (
              <Box mt="sm">
                <ErrorNotification
                  errorMessage="Please check this box to continue."
                  tabIndex={-1}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
      <ButtonGroup>
        <Button
          allytmln="Submit Delete IRA Beneficiary"
          text={
            removeBeneficiary === 'no'
              ? "Don't Remove"
              : 'Yes, Remove Beneficiary'
          }
          variant="primary"
          data-testid="continue-remove-beneficiary-button"
          onClick={
            removeBeneficiary === 'no'
              ? handleCloseModal
              : deleteBeneficiaryClick
          }
          loading={isLoading}
        />
        <Button
          allytmln="Cancel Delete IRA Beneficiary"
          text="Cancel"
          variant="link"
          data-testid="delete-cancel-button"
          onClick={handleCloseModal}
        />
      </ButtonGroup>
    </Space>
  )
}
